import { DOC_PREFIX, type DocPrefixKey } from '../constants.js';
import { getFiscalYear, type DateInput } from './date.js';

export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** `Manish Dhaduk` → `MD`; falls back to the first character. */
export const initials = (name: string | null | undefined, max = 2): string => {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, max)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

export const truncate = (value: string | null | undefined, length = 60): string => {
  if (!value) return '';
  return value.length <= length ? value : `${value.slice(0, length - 1).trimEnd()}…`;
};

/**
 * Compose a document number: `QTN/25-26/0042`.
 *
 * The sequence itself is allocated in a transaction by the API's numbering
 * service; this only formats what it hands back.
 */
export const formatDocumentNumber = (
  prefix: DocPrefixKey,
  sequence: number,
  options?: { date?: DateInput; padTo?: number; separator?: string },
): string => {
  const { date, padTo = 4, separator = '/' } = options ?? {};
  const fy = getFiscalYear(date ?? new Date());
  return [DOC_PREFIX[prefix], fy.shortLabel, String(sequence).padStart(padTo, '0')].join(separator);
};

/** Strip everything but digits — for phone, GSTIN and Aadhaar normalisation. */
export const digitsOnly = (value: string | null | undefined): string =>
  value ? value.replace(/\D/g, '') : '';

export const normaliseEmail = (value: string): string => value.trim().toLowerCase();

/** Serial numbers are compared case-insensitively and without separators. */
export const normaliseSerial = (value: string): string =>
  value.trim().toUpperCase().replace(/[\s-]+/g, '');

/** Safe filename for generated exports and PDFs. */
export const toFileSlug = (value: string): string =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
